import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
type LegalDoc = 'terminos' | 'privacidad' | null;
const LEGAL_CONTENT: Record<Exclude<LegalDoc, null>, {
  title: string;
  sections: {
    heading: string;
    body: string;
  }[];
}> = {
  terminos: {
    title: 'Términos y Condiciones',
    sections: [{
      heading: '1. Naturaleza del producto',
      body: 'El "Protocolo 7 Días Sin Reinfestación: El Método MIC" es un producto digital descargable (PDF). Al completar tu compra recibes acceso inmediato al material para su descarga. No se trata de un producto físico ni de un servicio de atención personalizada.'
    }, {
      heading: '2. Uso de la información',
      body: 'El contenido tiene fines exclusivamente educativos e informativos y no sustituye el consejo, diagnóstico o tratamiento médico profesional. Los resultados pueden variar según la correcta y constante aplicación del protocolo por parte de cada usuario.'
    }, {
      heading: '3. Licencia de uso',
      body: 'Tu compra te otorga una licencia personal e intransferible para uso doméstico. Queda prohibida la reventa, redistribución, copia o publicación total o parcial del material sin autorización expresa por escrito.'
    }, {
      heading: '4. Pagos y acceso',
      body: 'El pago es único, sin suscripciones ni cargos recurrentes. Una vez confirmado el pago, el acceso de descarga se habilita de forma inmediata las 24 horas del día.'
    }, {
      heading: '5. Limitación de responsabilidad',
      body: 'No garantizamos resultados específicos, ya que dependen de factores individuales. El usuario asume la responsabilidad de aplicar el protocolo siguiendo las indicaciones del manual y, ante cualquier condición médica, consultar a un profesional de la salud.'
    }]
  },
  privacidad: {
    title: 'Política de Privacidad',
    sections: [{
      heading: '1. Información que recopilamos',
      body: 'Recopilamos únicamente los datos necesarios para procesar tu compra y entregarte el producto digital, como tu nombre y la información de pago gestionada de forma segura por nuestra plataforma de pagos.'
    }, {
      heading: '2. Uso de tus datos',
      body: 'Tus datos se utilizan exclusivamente para completar la transacción, habilitar el acceso a la descarga y dar soporte relacionado con tu compra. No vendemos ni alquilamos tu información a terceros.'
    }, {
      heading: '3. Seguridad',
      body: 'Implementamos medidas razonables de seguridad para proteger tu información. Los pagos se procesan a través de pasarelas encriptadas y certificadas que cumplen con los estándares de la industria.'
    }, {
      heading: '4. Cookies',
      body: 'El sitio puede utilizar cookies y tecnologías similares para mejorar la experiencia de navegación y medir el rendimiento de la página. Puedes deshabilitarlas desde la configuración de tu navegador.'
    }, {
      heading: '5. Tus derechos',
      body: 'Tienes derecho a solicitar el acceso, la rectificación o la eliminación de tus datos personales conforme a la legislación aplicable en materia de protección de datos.'
    }]
  }
};
function LegalModal({
  doc,
  onClose
}: {
  doc: Exclude<LegalDoc, null>;
  onClose: () => void;
}) {
  const content = LEGAL_CONTENT[doc];
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  return <motion.div className="fixed inset-0 z-[60] flex items-center justify-center p-4" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="legal-title" data-id="element-48">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" data-id="element-49" />

      <motion.div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" initial={{
      opacity: 0,
      scale: 0.96,
      y: 12
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96,
      y: 12
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 28
    }} onClick={e => e.stopPropagation()} data-id="element-50">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50" data-id="element-51">
          <h3 id="legal-title" className="text-lg font-bold text-slate-900" data-id="element-52">
            {content.title}
          </h3>
          <button onClick={onClose} aria-label="Cerrar" className="text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg p-1.5 transition-colors" data-id="element-53">
            <XIcon className="w-5 h-5" data-id="element-54" />
          </button>
        </div>

        <div className="px-6 py-6 overflow-y-auto space-y-6" data-id="element-55">
          {content.sections.map((s, i) => <div key={i} data-id="element-56">
              <h4 className="font-bold text-slate-900 mb-1.5" data-id="element-57">{s.heading}</h4>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-58">{s.body}</p>
            </div>)}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end" data-id="element-59">
          <button onClick={onClose} className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 py-2 rounded-lg transition-colors" data-id="element-60">
            Entendido
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [openDoc, setOpenDoc] = useState<LegalDoc>(null);
  return <footer className="bg-slate-950 text-slate-500 py-10 text-center text-sm px-4" data-id="element-61">
      <p data-id="element-62">
        © {new Date().getFullYear()} Método MIC. Todos los derechos reservados.
      </p>
      <p className="mt-2 text-xs max-w-2xl mx-auto opacity-70" data-id="element-63">
        Este producto no sustituye el consejo médico profesional. Los resultados
        pueden variar dependiendo de la correcta aplicación del protocolo.
      </p>

      <nav className="mt-5 flex items-center justify-center gap-6" data-id="element-64">
        <button onClick={() => setOpenDoc('terminos')} className="text-slate-400 hover:text-white underline-offset-4 hover:underline transition-colors" data-id="element-65">
          Términos y Condiciones
        </button>
        <span className="text-slate-700" data-id="element-66">·</span>
        <button onClick={() => setOpenDoc('privacidad')} className="text-slate-400 hover:text-white underline-offset-4 hover:underline transition-colors" data-id="element-67">
          Política de Privacidad
        </button>
      </nav>

      <AnimatePresence data-id="element-68">
        {openDoc && <LegalModal doc={openDoc} onClose={() => setOpenDoc(null)} data-id="element-69" />}
      </AnimatePresence>
    </footer>;
}